import { OrderData, OrderStatus, SchedulerConfigurationData, UnavailabilityData } from '../../api';
import { maskToIndexes } from '../../utils/maskUtils';
import { seatedIndexesInConfig } from './orderSeating';

/**
 * Which scheduler resources can actually take a booking over a given span.
 *
 * The resource picker used to present every resource as equally selectable, because it had no
 * date or time and so no way to know. This derives the real answer from the day's orders and
 * blocks, using the same relevance rule the backend applies when it validates a manual
 * assignment: an order counts against a config through the tables it is seated on in that config.
 */

export enum ResourceAvailability {
  FREE = 'free',
  OCCUPIED = 'occupied',
  BLOCKED = 'blocked',
}

export type ResourceAvailabilityInfo = {
  type: ResourceAvailability;
  /** Orders holding the resource over the span — shown so staff know what is in the way. */
  orderNumbers: number[];
};

export type ResourceAvailabilityMap = Record<number, ResourceAvailabilityInfo>;

const INACTIVE_STATUSES: OrderStatus[] = [OrderStatus.ARCHIVED, OrderStatus.ABANDONED];

const overlaps = (startA: number, endA: number, startB: number, endB: number) => startA < endB && startB < endA;

export const resourceAvailabilityForSpan = ({
  config,
  orders,
  unavailabilities,
  startSeconds,
  endSeconds,
  excludeOrderId,
}: {
  config: SchedulerConfigurationData;
  orders: OrderData[];
  unavailabilities: UnavailabilityData[];
  startSeconds: number;
  endSeconds: number;
  /** The order being edited — its own resources must not read as taken. */
  excludeOrderId?: string;
}): ResourceAvailabilityMap => {
  const availability: ResourceAvailabilityMap = {};
  for (let index = 0; index < config.resource_count; index++) {
    availability[index] = { type: ResourceAvailability.FREE, orderNumbers: [] };
  }

  orders
    .filter((order) => order.id !== excludeOrderId)
    .filter((order) => !INACTIVE_STATUSES.includes(order.status))
    .filter((order) => overlaps(order.time ?? 0, (order.time ?? 0) + order.duration, startSeconds, endSeconds))
    .forEach((order) => {
      seatedIndexesInConfig(order, config).forEach((index) => {
        const entry = availability[index];
        if (!entry) {
          return;
        }
        entry.type = ResourceAvailability.OCCUPIED;
        if (order.order_number !== undefined && !entry.orderNumbers.includes(order.order_number)) {
          entry.orderNumbers.push(order.order_number);
        }
      });
    });

  // Blocked wins over occupied: the backend excludes blocked bits from its occupied count too.
  unavailabilities
    .filter(
      (unavailability) =>
        unavailability.all_day ||
        overlaps(unavailability.start_time, unavailability.end_time, startSeconds, endSeconds),
    )
    .forEach((unavailability) => {
      (unavailability.shared_resources_blocks?.scheduler_configs ?? [])
        .filter((block) => block.scheduler_config_id === config.id)
        .forEach((block) => {
          maskToIndexes(block.blocked_mask).forEach((index) => {
            if (availability[index]) {
              availability[index] = { type: ResourceAvailability.BLOCKED, orderNumbers: [] };
            }
          });
        });
    });

  return availability;
};

export const countFree = (availability: ResourceAvailabilityMap, indexes: number[]): number =>
  indexes.filter((index) => availability[index]?.type === ResourceAvailability.FREE).length;
